import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ActivityFormData, EMPTY_ACTIVITY_FORM, ActivityPhoto } from "../types/activity";
import { API_URL } from "../config";

// ===================== COMPOSANTS SKELETON =====================

// Skeleton pour une carte (section)
const SkeletonCard = ({ children }: { children: React.ReactNode }) => (
  <div 
    style={{
      background: "#ffffff",
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 4px 16px -2px rgba(0, 0, 0, 0.03)",
      marginBottom: "24px",
      overflow: "hidden",
    }}
  >
    {children}
  </div>
);

// Skeleton pour l'en-tête d'une carte
const SkeletonCardHeader = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "14px",
      padding: "20px 24px",
      background: "#ffffff",
      borderBottom: "1px solid #f1f5f9",
    }}
  >
    <div
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "10px",
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        flexShrink: 0,
      }}
    />
    <div>
      <div
        style={{
          width: "140px",
          height: "18px",
          borderRadius: "6px",
          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite",
          marginBottom: "6px",
        }}
      />
      <div
        style={{
          width: "220px",
          height: "14px",
          borderRadius: "6px",
          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite",
        }}
      />
    </div>
  </div>
);

// Skeleton pour un champ de formulaire
const SkeletonField = ({ labelWidth = 120, inputHeight = 42 }: { labelWidth?: number; inputHeight?: number }) => (
  <div style={{ marginBottom: "16px" }}>
    <div
      style={{
        width: labelWidth,
        height: "16px",
        borderRadius: "4px",
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        marginBottom: "8px",
      }}
    />
    <div
      style={{
        width: "100%",
        height: inputHeight,
        borderRadius: "8px",
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
  </div>
);

// Skeleton pour une ligne de liste (inclusion / itinerary)
const SkeletonListItem = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      borderBottom: "1px solid #f1f5f9",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div
        style={{
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite",
        }}
      />
      <div
        style={{
          width: "200px",
          height: "16px",
          borderRadius: "4px",
          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite",
        }}
      />
    </div>
    <div
      style={{
        width: "30px",
        height: "30px",
        borderRadius: "6px",
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
  </div>
);

// Skeleton pour une photo
const SkeletonPhoto = () => (
  <div
    style={{
      width: "100%",
      aspectRatio: "1/1",
      borderRadius: "8px",
      background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s infinite",
    }}
  />
);

// Skeleton principal pour la page d'édition
const EditActivitySkeleton = () => {
  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "24px 20px 40px", boxSizing: "border-box" }}>
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          {/* En-tête */}
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <div
                  style={{
                    width: "80px",
                    height: "16px",
                    borderRadius: "4px",
                    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                  }}
                />
                <i className="fa-solid fa-chevron-right" style={{ fontSize: "10px", color: "#94a3b8" }} />
                <div
                  style={{
                    width: "100px",
                    height: "16px",
                    borderRadius: "4px",
                    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                  }}
                />
              </div>
              <div
                style={{
                  width: "200px",
                  height: "28px",
                  borderRadius: "6px",
                  background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s infinite",
                  marginBottom: "6px",
                }}
              />
              <div
                style={{
                  width: "300px",
                  height: "16px",
                  borderRadius: "4px",
                  background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.5s infinite",
                }}
              />
            </div>
            <div
              style={{
                width: "160px",
                height: "42px",
                borderRadius: "8px",
                background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.5s infinite",
              }}
            />
          </div>

          <div className="row g-4">
            {/* Colonne principale */}
            <div className="col-12 col-lg-8">
              {/* Basic Info Skeleton */}
              <SkeletonCard>
                <SkeletonCardHeader />
                <div style={{ padding: "24px" }}>
                  <div className="row g-3">
                    <div className="col-12">
                      <SkeletonField labelWidth={120} inputHeight={42} />
                    </div>
                    <div className="col-12">
                      <SkeletonField labelWidth={100} inputHeight={90} />
                    </div>
                    <div className="col-12 col-md-4">
                      <SkeletonField labelWidth={60} inputHeight={42} />
                    </div>
                  </div>
                </div>
              </SkeletonCard>

              {/* Inclusions Skeleton */}
              <SkeletonCard>
                <SkeletonCardHeader />
                <div style={{ padding: "24px" }}>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div
                        style={{
                          width: "100%",
                          height: "42px",
                          borderRadius: "8px",
                          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.5s infinite",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: "140px",
                        height: "42px",
                        borderRadius: "8px",
                        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.5s infinite",
                      }}
                    />
                  </div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden" }}>
                    {[...Array(3)].map((_, i) => (
                      <SkeletonListItem key={i} />
                    ))}
                  </div>
                </div>
              </SkeletonCard>

              {/* Itinerary Skeleton */}
              <SkeletonCard>
                <SkeletonCardHeader />
                <div style={{ padding: "24px" }}>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
                    <div style={{ width: "130px" }}>
                      <div
                        style={{
                          width: "100%",
                          height: "42px",
                          borderRadius: "8px",
                          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.5s infinite",
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div
                        style={{
                          width: "100%",
                          height: "42px",
                          borderRadius: "8px",
                          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.5s infinite",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: "120px",
                        height: "42px",
                        borderRadius: "8px",
                        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.5s infinite",
                      }}
                    />
                  </div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden" }}>
                    {[...Array(3)].map((_, i) => (
                      <SkeletonListItem key={i} />
                    ))}
                  </div>
                </div>
              </SkeletonCard>

              {/* Photos Skeleton */}
              <SkeletonCard>
                <SkeletonCardHeader />
                <div style={{ padding: "24px" }}>
                  <div
                    style={{
                      width: "120px",
                      height: "16px",
                      borderRadius: "4px",
                      background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.5s infinite",
                      marginBottom: "12px",
                    }}
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: "12px", marginBottom: "20px" }}>
                    {[...Array(4)].map((_, i) => (
                      <SkeletonPhoto key={i} />
                    ))}
                  </div>

                  <div
                    style={{
                      width: "140px",
                      height: "16px",
                      borderRadius: "4px",
                      background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.5s infinite",
                      marginBottom: "12px",
                    }}
                  />
                  <div
                    style={{
                      width: "100%",
                      height: "120px",
                      borderRadius: "10px",
                      border: "2px dashed #e2e8f0",
                      background: "linear-gradient(90deg, #f8fafc 25%, #f0f0f0 50%, #f8fafc 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.5s infinite",
                    }}
                  />
                </div>
              </SkeletonCard>
            </div>

            {/* Sidebar Skeleton */}
            <div className="col-12 col-lg-4">
              <div
                style={{
                  position: "sticky",
                  top: "20px",
                  background: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 16px -2px rgba(0, 0, 0, 0.04)",
                  padding: "24px",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "16px",
                    borderRadius: "4px",
                    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                    marginBottom: "16px",
                  }}
                />

                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    padding: "16px",
                    border: "1px solid #e2e8f0",
                    marginBottom: "20px",
                  }}
                >
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 0",
                        borderBottom: i < 2 ? "1px dashed #e2e8f0" : "none",
                      }}
                    >
                      <div
                        style={{
                          width: "60px",
                          height: "14px",
                          borderRadius: "4px",
                          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.5s infinite",
                        }}
                      />
                      <div
                        style={{
                          width: "100px",
                          height: "16px",
                          borderRadius: "4px",
                          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.5s infinite",
                        }}
                      />
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    width: "100%",
                    height: "42px",
                    borderRadius: "8px",
                    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                    marginBottom: "10px",
                  }}
                />
                <div
                  style={{
                    width: "100%",
                    height: "42px",
                    borderRadius: "8px",
                    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ===================== COMPOSANT PRINCIPAL =====================

const EditActivity = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [formData, setFormData] = useState<ActivityFormData>({ ...EMPTY_ACTIVITY_FORM });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const [inclusionInput, setInclusionInput] = useState("");
  const [itiTime, setItiTime] = useState("");
  const [itiLabel, setItiLabel] = useState("");

  // Photos existantes (déjà en base)
  const [existingPhotos, setExistingPhotos] = useState<ActivityPhoto[]>([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState<number[]>([]);

  // Nouvelles photos à uploader
  const [newPhotos, setNewPhotos] = useState<File[]>([]);
  const [newPhotoPreviews, setNewPhotoPreviews] = useState<string[]>([]);

  const token = localStorage.getItem("token_admin_silverjarz");
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const response = await axios.get(`${API_URL}/activities/${id}`, config);
        const activity = response.data.data;

        const itineraryArray = activity.itinerary
          ? Object.entries(activity.itinerary).map(([time, label]) => ({ time, label: String(label) }))
          : [];

        setFormData({
          name: activity.name || "",
          description: activity.description || "",
          price: String(activity.price ?? ""),
          inclusions: activity.inclusions || [],
          itinerary: itineraryArray,
        });
        setExistingPhotos(activity.photos || []);
      } catch (error) {
        console.error("Error fetching activity:", error);
        toast.error("Couldn't load activity");
      } finally {
        setIsFetching(false);
      }
    };
    fetchActivity();
  }, [id]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Inclusions ──────────────────────────────────────────────
  const handleAddInclusion = () => {
    if (!inclusionInput.trim()) {
      toast.error("Inclusion text is required");
      return;
    }
    setFormData((prev) => ({ ...prev, inclusions: [...prev.inclusions, inclusionInput.trim()] }));
    setInclusionInput("");
  };

  const handleRemoveInclusion = (index: number) => {
    setFormData((prev) => ({ ...prev, inclusions: prev.inclusions.filter((_, i) => i !== index) }));
  };

  // ── Itinerary ───────────────────────────────────────────────
  const handleAddItineraryStep = () => {
    if (!itiTime.trim() || !itiLabel.trim()) {
      toast.error("Time and label are required for an itinerary step");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      itinerary: [...prev.itinerary, { time: itiTime.trim(), label: itiLabel.trim() }],
    }));
    setItiTime("");
    setItiLabel("");
  };

  const handleRemoveItineraryStep = (index: number) => {
    setFormData((prev) => ({ ...prev, itinerary: prev.itinerary.filter((_, i) => i !== index) }));
  };

  // ── Photos existantes ───────────────────────────────────────
  const handleRemoveExistingPhoto = (photoId: number) => {
    setExistingPhotos((prev) => prev.filter((p) => p.id !== photoId));
    setDeletedPhotoIds((prev) => [...prev, photoId]);
  };

  // ── Nouvelles photos ────────────────────────────────────────
  const handleNewPhotosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setNewPhotos((prev) => [...prev, ...files]);
    setNewPhotoPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    e.target.value = "";
  };

  const handleRemoveNewPhoto = (index: number) => {
    setNewPhotos((prev) => prev.filter((_, i) => i !== index));
    setNewPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Submit ──────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.description || !formData.price) {
      toast.error("Please fill in all required fields before saving");
      return;
    }

    setIsLoading(true);
    try {
      const payload = new FormData();
      payload.append("_method", "POST");
      payload.append("name", formData.name);
      payload.append("description", formData.description);
      payload.append("price", formData.price);

      const itineraryObject = formData.itinerary.reduce((acc, step) => {
        acc[step.time] = step.label;
        return acc;
      }, {} as Record<string, string>);

      payload.append("inclusions", JSON.stringify(formData.inclusions));
      payload.append("itinerary", JSON.stringify(itineraryObject));
      payload.append("deleted_photo_ids", JSON.stringify(deletedPhotoIds));

      newPhotos.forEach((file) => payload.append("photos[]", file));

      const response = await axios.post(`${API_URL}/activities/${id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        toast.success("Activity updated successfully!");
        navigate("/admin/list-activities");
      }
    } catch (error: any) {
      console.error("Error updating activity:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Error updating activity");
    } finally {
      setIsLoading(false);
    }
  };

  // Afficher le skeleton pendant le chargement
  if (isFetching) {
    return <EditActivitySkeleton />;
  }

  return (
    <>
      <style>{`
        .act-container { max-width: 1280px; margin: 0 auto; }
        .act-card { background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0 !important; box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.03); margin-bottom: 24px; transition: border-color 0.2s ease; }
        .act-card:hover { border-color: #cbd5e1; }
        .act-card-head { display: flex; align-items: center; gap: 14px; padding: 20px 24px; background: #ffffff; border-bottom: 1px solid #f1f5f9; }
        .act-card-icon { width: 40px; height: 40px; border-radius: 10px; background: rgba(41, 75, 90, 0.08); color: #294b5a; display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
        .act-card-title { font-size: 15px; font-weight: 700; color: #294b5a; letter-spacing: -0.01em; }
        .act-card-sub { font-size: 12.5px; color: #64748b; margin-top: 2px; }
        .act-card-body { padding: 24px; }
        .act-label { display: block; font-size: 12.5px; font-weight: 600; color: #334155; margin-bottom: 7px; }
        .act-label .req { color: #dc2626; margin-left: 2px; }
        .act-input-wrap { position: relative; }
        .act-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 13px; pointer-events: none; }
        .act-input, .act-textarea { width: 100%; font-size: 13.5px; color: #0f172a; border: 1px solid #cbd5e1 !important; border-radius: 8px; background: #f8fafc; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
        .act-input { height: 42px; padding: 0 14px 0 38px; }
        .act-textarea { padding: 12px 14px; resize: vertical; line-height: 1.5; min-height: 90px; }
        .act-input::placeholder, .act-textarea::placeholder { color: #94a3b8; }
        .act-input:focus, .act-textarea:focus { outline: none; border-color: #294b5a; background: #ffffff; box-shadow: 0 0 0 3px rgba(41, 75, 90, 0.1); }
        .act-fee-currency { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); font-size: 12.5px; font-weight: 700; color: #294b5a; pointer-events: none; }
        .act-fee-input { padding-left: 40px !important; font-weight: 600; }
        .act-doc-add { display: flex; gap: 10px; flex-wrap: wrap; }
        .act-doc-add-btn { height: 42px; padding: 0 20px; border-radius: 8px; border: none; background: #294b5a; color: #ffffff; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; white-space: nowrap; transition: all 0.2s ease; }
        .act-doc-add-btn:hover { background: #1e3743; }
        .act-doc-list { margin-top: 16px; border: 1px solid #e2e8f0 !important; border-radius: 10px; overflow: hidden; }
        .act-doc-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; font-size: 13.5px; color: #1e293b; background: #ffffff; }
        .act-doc-row:hover { background: #f8fafc; }
        .act-doc-row + .act-doc-row { border-top: 1px solid #f1f5f9; }
        .act-doc-num { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: rgba(211, 190, 118, 0.2); color: #8c7329; font-size: 11px; font-weight: 700; margin-right: 12px; flex-shrink: 0; }
        .act-doc-remove { width: 30px; height: 30px; border-radius: 6px; border: 1px solid transparent !important; background: transparent; color: #94a3b8; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s ease; }
        .act-doc-remove:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
        .act-sidebar-card { position: sticky; top: 20px; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0 !important; box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.04); padding: 24px; }
        .act-summary-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px dashed #e2e8f0; font-size: 13px; }
        .act-summary-item:last-child { border-bottom: none; }
        .act-btn { height: 42px; padding: 0 24px; font-size: 13.5px; font-weight: 600; border-radius: 8px; cursor: pointer; border: none; display: inline-flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); text-decoration: none; }
        .act-btn--ghost { background: #ffffff; color: #475569; border: 1px solid #cbd5e1 !important; }
        .act-btn--ghost:hover { background: #f8fafc; color: #0f172a; border-color: #94a3b8; }
        .act-btn--primary { background: #294b5a; color: #ffffff; box-shadow: 0 2px 8px rgba(41, 75, 90, 0.25); width: 100%; }
        .act-btn--primary:hover:not(:disabled) { background: #1e3743; box-shadow: 0 4px 12px rgba(41, 75, 90, 0.35); }
        .act-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .act-btn--primary i { color: #d3be76; }
        .act-photo-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 12px; margin-top: 16px; }
        .act-photo-thumb { position: relative; width: 100%; aspect-ratio: 1 / 1; border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0; }
        .act-photo-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .act-photo-remove { position: absolute; top: 4px; right: 4px; width: 22px; height: 22px; border-radius: 50%; border: none; background: rgba(15, 23, 42, 0.65); color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; }
        .act-photo-remove:hover { background: #dc2626; }
        .act-upload-zone { border: 2px dashed #cbd5e1; border-radius: 10px; padding: 24px; text-align: center; cursor: pointer; transition: all 0.15s ease; background: #f8fafc; }
        .act-upload-zone:hover { border-color: #294b5a; background: #ffffff; }
        .act-section-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin: 20px 0 8px; }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "24px 20px 40px", boxSizing: "border-box" }}>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="act-container">
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: "13px", color: "#64748b" }}>
                <Link to="/admin/list-activities" style={{ color: "#64748b", textDecoration: "none", fontWeight: 500 }}>Activities</Link>
                <i className="fa-solid fa-chevron-right" style={{ fontSize: "10px", color: "#94a3b8" }}></i>
                <span style={{ color: "#294b5a", fontWeight: 600 }}>Edit Activity</span>
              </div>
              <h1 style={{ color: "#294b5a", fontSize: "22px", fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>Edit Activity</h1>
              <p style={{ color: "#64748b", fontSize: "13.5px", margin: "4px 0 0" }}>Update the details, inclusions, itinerary and photos.</p>
            </div>
            <Link to="/admin/list-activities" className="act-btn act-btn--ghost">
              <i className="fa-solid fa-arrow-left"></i>
              Back to Activities
            </Link>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              <div className="col-12 col-lg-8">
                {/* BASIC INFO */}
                <div className="act-card">
                  <div className="act-card-head">
                    <div className="act-card-icon"><i className="fa-solid fa-compass"></i></div>
                    <div>
                      <div className="act-card-title">Basic Information</div>
                      <div className="act-card-sub">Name, description and pricing</div>
                    </div>
                  </div>
                  <div className="act-card-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="act-label">Activity Name<span className="req">*</span></label>
                        <div className="act-input-wrap">
                          <i className="fa-solid fa-heading act-input-icon"></i>
                          <input type="text" name="name" className="act-input"
                            value={formData.name} onChange={handleInputChange} required />
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="act-label">Description<span className="req">*</span></label>
                        <textarea name="description" className="act-textarea"
                          value={formData.description} onChange={handleInputChange} required />
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="act-label">Price<span className="req">*</span></label>
                        <div className="act-input-wrap">
                          <span className="act-fee-currency">OMR</span>
                          <input type="number" name="price" className="act-input act-fee-input" step="0.01" min="0"
                            value={formData.price} onChange={handleInputChange} required />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* INCLUSIONS */}
                <div className="act-card">
                  <div className="act-card-head">
                    <div className="act-card-icon"><i className="fa-solid fa-list-check"></i></div>
                    <div>
                      <div className="act-card-title">Inclusions</div>
                      <div className="act-card-sub">What's included in this activity</div>
                    </div>
                  </div>
                  <div className="act-card-body">
                    <div className="act-doc-add">
                      <div className="act-input-wrap" style={{ flex: 1, minWidth: 220 }}>
                        <i className="fa-solid fa-check act-input-icon"></i>
                        <input type="text" className="act-input" placeholder="e.g. BBQ Dinner"
                          value={inclusionInput} onChange={(e) => setInclusionInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddInclusion(); } }} />
                      </div>
                      <button type="button" onClick={handleAddInclusion} className="act-doc-add-btn">
                        <i className="fa-solid fa-plus"></i>
                        Add Inclusion
                      </button>
                    </div>

                    <div className="act-doc-list">
                      {formData.inclusions.length > 0 ? (
                        formData.inclusions.map((item, index) => (
                          <div key={index} className="act-doc-row">
                            <div className="d-flex align-items-center">
                              <span className="act-doc-num">{index + 1}</span>
                              <span style={{ fontWeight: 500 }}>{item}</span>
                            </div>
                            <button type="button" onClick={() => handleRemoveInclusion(index)} className="act-doc-remove" title="Remove">
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: "32px 16px", textAlign: "center", color: "#94a3b8", fontSize: "13.5px" }}>
                          No inclusions added yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ITINERARY */}
                <div className="act-card">
                  <div className="act-card-head">
                    <div className="act-card-icon"><i className="fa-solid fa-route"></i></div>
                    <div>
                      <div className="act-card-title">Itinerary</div>
                      <div className="act-card-sub">Step by step schedule of the activity</div>
                    </div>
                  </div>
                  <div className="act-card-body">
                    <div className="act-doc-add">
                      <div className="act-input-wrap" style={{ width: 130 }}>
                        <i className="fa-solid fa-clock act-input-icon"></i>
                        <input type="text" className="act-input" placeholder="10:00"
                          value={itiTime} onChange={(e) => setItiTime(e.target.value)} />
                      </div>
                      <div className="act-input-wrap" style={{ flex: 1, minWidth: 220 }}>
                        <i className="fa-solid fa-flag act-input-icon"></i>
                        <input type="text" className="act-input" placeholder="e.g. Departure"
                          value={itiLabel} onChange={(e) => setItiLabel(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddItineraryStep(); } }} />
                      </div>
                      <button type="button" onClick={handleAddItineraryStep} className="act-doc-add-btn">
                        <i className="fa-solid fa-plus"></i>
                        Add Step
                      </button>
                    </div>

                    <div className="act-doc-list">
                      {formData.itinerary.length > 0 ? (
                        formData.itinerary.map((step, index) => (
                          <div key={index} className="act-doc-row">
                            <div className="d-flex align-items-center">
                              <span className="act-doc-num">{index + 1}</span>
                              <span style={{ fontWeight: 700, color: "#294b5a", marginRight: 10 }}>{step.time}</span>
                              <span style={{ fontWeight: 500 }}>{step.label}</span>
                            </div>
                            <button type="button" onClick={() => handleRemoveItineraryStep(index)} className="act-doc-remove" title="Remove">
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: "32px 16px", textAlign: "center", color: "#94a3b8", fontSize: "13.5px" }}>
                          No itinerary steps added yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* PHOTOS */}
                <div className="act-card">
                  <div className="act-card-head">
                    <div className="act-card-icon"><i className="fa-solid fa-images"></i></div>
                    <div>
                      <div className="act-card-title">Photos</div>
                      <div className="act-card-sub">Manage existing photos and upload new ones</div>
                    </div>
                  </div>
                  <div className="act-card-body">
                    {existingPhotos.length > 0 && (
                      <>
                        <div className="act-section-label">Current photos</div>
                        <div className="act-photo-grid">
                          {existingPhotos.map((photo) => (
                            <div key={photo.id} className="act-photo-thumb">
                              <img src={`${API_URL.replace("/api", "")}/storage/${photo.path}`} alt={`photo-${photo.id}`} />
                              <button type="button" className="act-photo-remove" onClick={() => handleRemoveExistingPhoto(photo.id)}>
                                <i className="fa-solid fa-xmark"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    <div className="act-section-label">Add new photos</div>
                    <label htmlFor="photo-upload" className="act-upload-zone">
                      <i className="fa-solid fa-cloud-arrow-up mb-2" style={{ fontSize: 22, color: "#294b5a", display: "block" }}></i>
                      <span style={{ fontSize: 13.5, color: "#475569", fontWeight: 600 }}>Click to upload photos</span>
                      <input id="photo-upload" type="file" accept="image/*" multiple onChange={handleNewPhotosChange} style={{ display: "none" }} />
                    </label>

                    {newPhotoPreviews.length > 0 && (
                      <div className="act-photo-grid">
                        {newPhotoPreviews.map((src, index) => (
                          <div key={index} className="act-photo-thumb">
                            <img src={src} alt={`new-preview-${index}`} />
                            <button type="button" className="act-photo-remove" onClick={() => handleRemoveNewPhoto(index)}>
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* SIDEBAR */}
              <div className="col-12 col-lg-4">
                <div className="act-sidebar-card">
                  <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#94a3b8", marginBottom: "12px" }}>
                    Summary
                  </div>

                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "8px", padding: "16px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
                    <div className="act-summary-item">
                      <span style={{ color: "#64748b" }}>Name</span>
                      <span style={{ fontWeight: 600, color: "#0f172a", maxWidth: "150px", textAlign: "right" }} className="text-truncate">
                        {formData.name || "—"}
                      </span>
                    </div>
                    <div className="act-summary-item">
                      <span style={{ color: "#64748b" }}>Price</span>
                      <span style={{ fontWeight: 600, color: "#294b5a" }}>{formData.price ? `${formData.price} OMR` : "—"}</span>
                    </div>
                    <div className="act-summary-item">
                      <span style={{ color: "#64748b" }}>Photos</span>
                      <span style={{ fontWeight: 600, color: "#0f172a" }}>{existingPhotos.length + newPhotos.length}</span>
                    </div>
                  </div>

                  <button type="submit" disabled={isLoading} className="act-btn act-btn--primary mb-2">
                    <i className="fa-solid fa-check"></i>
                    {isLoading ? "Saving Changes..." : "Save Changes"}
                  </button>

                  <Link to="/admin/list-activities" className="act-btn act-btn--ghost" style={{ width: "100%" }}>
                    Cancel
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditActivity;